import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  BookMarked,
  BookOpen,
  GraduationCap,
  Headphones,
  History,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  type LucideIcon,
  Menu,
  Mic,
  PenLine,
  Settings,
  X,
} from 'lucide-react';
import { api } from '@/api/client';
import { Wordmark } from '@/components/Wordmark';
import { PlanStatusBar, PlanStatusPill } from '@/components/ui/PlanStatus';
import { NotificationBell, useUnreadCount } from '@/features/notifications/NotificationBell';
import { VerifyEmailBanner } from '@/features/account/VerifyEmailBanner';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';
import { authClient } from '@/lib/auth-client';
import { examKeyFromFamilySlug, useTheme } from '@/lib/theme';
import { useCan, useIsStaff } from '@/features/admin-content/use-current-user';
import { useClaimTaster } from '@/features/taster/useClaimTaster';
import { StaffShell } from './StaffShell';
import { Disclaimer, SkipLink } from './shell-parts';

type PracticeSearch = {
  variant: undefined;
  section: 'listening' | 'reading' | 'writing' | 'speaking' | 'mocks';
};

interface NavEntry {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly to: string;
  readonly search?: Record<string, unknown>;
}

interface NavGroup {
  readonly label: string | null;
  readonly items: readonly NavEntry[];
}

/** Sidebar structure, in the order the design system specifies. */
const NAV: readonly NavGroup[] = [
  {
    label: null,
    items: [{ label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard' }],
  },
  {
    label: 'Practice',
    items: [
      {
        label: 'Listening',
        icon: Headphones,
        to: '/practice',
        search: { variant: undefined, section: 'listening' },
      },
      {
        label: 'Reading',
        icon: BookOpen,
        to: '/practice',
        search: { variant: undefined, section: 'reading' },
      },
      {
        label: 'Writing',
        icon: PenLine,
        to: '/practice',
        search: { variant: undefined, section: 'writing' },
      },
      {
        label: 'Speaking',
        icon: Mic,
        to: '/practice',
        search: { variant: undefined, section: 'speaking' },
      },
      {
        label: 'Full mock tests',
        icon: LayoutGrid,
        to: '/practice',
        search: { variant: undefined, section: 'mocks' },
      },
    ],
  },
  {
    label: 'Improve',
    items: [
      { label: 'Progress', icon: LineChart, to: '/progress', search: { variant: undefined } },
      { label: 'History', icon: History, to: '/history' },
      { label: 'Vocabulary', icon: BookMarked, to: '/vocabulary' },
      { label: 'Lessons', icon: GraduationCap, to: '/lessons' },
      { label: 'Speaking sessions', icon: Mic, to: '/speaking-sessions' },
    ],
  },
];

/** Bottom bar on phones — five destinations, 64px tall. */
const MOBILE_NAV: readonly NavEntry[] = [
  { label: 'Home', icon: LayoutDashboard, to: '/dashboard' },
  {
    label: 'Practice',
    icon: BookOpen,
    to: '/practice',
    search: { variant: undefined, section: 'reading' },
  },
  {
    label: 'Mocks',
    icon: LayoutGrid,
    to: '/practice',
    search: { variant: undefined, section: 'mocks' },
  },
  { label: 'Progress', icon: LineChart, to: '/progress', search: { variant: undefined } },
];

function useActive(entry: NavEntry): boolean {
  const location = useRouterState({ select: (s) => s.location });
  if (entry.to === '/admin') {
    return location.pathname === '/admin' || location.pathname === '/admin/';
  }
  if (!location.pathname.startsWith(entry.to)) return false;
  const section = entry.search?.['section'];
  if (typeof section === 'string') {
    return (location.search as Record<string, unknown>)['section'] === section;
  }
  return true;
}

function SidebarLink({
  entry,
  onNavigate,
}: {
  entry: NavEntry;
  onNavigate?: (() => void) | undefined;
}) {
  const active = useActive(entry);

  return (
    <Link
      // Route paths are validated by the generated route tree; the nav table above is the single source of truth.
      to={entry.to as '/dashboard'}
      search={(entry.search ?? {}) as PracticeSearch}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'focus-ring flex h-10 items-center gap-2.5 rounded-control px-3 text-[15px] transition-colors',
        active
          ? 'bg-primary-tint font-bold text-primary-hover'
          : 'text-ink-2 hover:bg-paper hover:text-ink',
      )}
    >
      <Icon icon={entry.icon} size={18} />
      {entry.label}
    </Link>
  );
}

/** Sets the exam accent colour from the student's chosen exam. Renders nothing. */
function ExamAccent() {
  const { data: session } = authClient.useSession();
  const { setExam } = useTheme();
  const profile = useQuery({
    queryKey: ['me-profile'],
    queryFn: () => api.get('/api/v1/me/profile'),
    enabled: Boolean(session),
    staleTime: 60_000,
  });
  const variant = profile.data?.targetExamVariantSlug ?? null;

  useEffect(() => {
    setExam(examKeyFromFamilySlug(variant));
  }, [variant, setExam]);

  return null;
}

/** Student portal navigation. Staff tools live in their own shell (`StaffShell`). */
function useSidebarGroups(): readonly NavGroup[] {
  const canPractice = useCan('attempt.create');
  if (canPractice) return NAV;
  return [
    {
      label: null,
      items: [{ label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard' }],
    },
  ];
}

function AccountFooter({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { data: session } = authClient.useSession();
  const name = session?.user.name || session?.user.email || 'Your account';
  const staff = useIsStaff();
  const unread = useUnreadCount();

  return (
    <div className="grid gap-1 border-t border-border pt-3">
      <Link
        to="/notifications"
        onClick={onNavigate}
        className="focus-ring flex h-10 items-center gap-2.5 rounded-control px-3 text-[15px] text-ink-2 hover:bg-paper hover:text-ink"
      >
        <Icon icon={Bell} size={18} />
        Notifications
        {unread > 0 ? (
          <span className="ml-auto rounded-full bg-danger px-1.5 text-[11px] font-bold leading-5 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </Link>
      {staff ? (
        <Link
          to="/admin"
          onClick={onNavigate}
          className="focus-ring flex h-10 items-center gap-2.5 rounded-control px-3 text-[15px] text-ink-2 hover:bg-paper hover:text-ink"
        >
          <Icon icon={Settings} size={18} />
          Staff settings
        </Link>
      ) : null}
      <Link
        to="/account"
        onClick={onNavigate}
        className="focus-ring flex items-center gap-2.5 rounded-control px-2 py-2 hover:bg-paper"
      >
        <Avatar name={name} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-small font-bold text-ink">{name}</span>
          <span className="block truncate text-overline font-normal text-muted">
            Account and billing
          </span>
        </span>
      </Link>
    </div>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const groups = useSidebarGroups();

  return (
    <div className="flex h-full flex-col gap-5 p-4">
      <Wordmark />
      <ExamAccent />

      <nav aria-label="Primary" className="flex-1 overflow-y-auto">
        {groups.map((group) => (
          <div key={group.label ?? 'root'} className="mb-4 grid gap-0.5">
            {group.label ? (
              <div className="eyebrow px-3 pb-1.5 text-muted">{group.label}</div>
            ) : null}
            {group.items.map((entry) => (
              <SidebarLink key={entry.label} entry={entry} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
      </nav>

      <AccountFooter onNavigate={onNavigate} />
    </div>
  );
}

/** Live exam attempt (not results). Test screens hide all app navigation. */
export function useIsExamTakingRoute(): boolean {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return /^\/attempts\/[^/]+$/.test(pathname);
}

const PUBLIC_PATHS = [
  '/',
  '/pricing',
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/accuracy',
  '/try',
];

/** Pages built on `AuthLayout`: one centred card that already carries the disclaimer. */
const AUTH_PATHS = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/onboarding'];

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { data: session } = authClient.useSession();
  const examMode = useIsExamTakingRoute();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  useClaimTaster(Boolean(session));
  const canPractice = useCan('attempt.create');
  const staff = useIsStaff();
  const mobileNav = canPractice
    ? MOBILE_NAV
    : MOBILE_NAV.filter((entry) => entry.to === '/dashboard');

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Test screens render edge to edge with no chrome (design system §13).
  if (examMode) {
    return (
      <div className="min-h-dvh bg-paper text-ink">
        <SkipLink />
        <main id="main-content">{children}</main>
      </div>
    );
  }

  // Onboarding is a short setup flow: logo bar only, no sidebar or app navigation.
  if (session && pathname === '/onboarding') {
    return (
      <div className="flex min-h-dvh flex-col bg-paper text-ink">
        <SkipLink />
        <header className="border-b border-border bg-surface">
          <div className="mx-auto flex h-16 w-full max-w-content items-center justify-between gap-3 px-4 sm:px-8">
            <Wordmark />
            <span className="hidden truncate text-small text-muted sm:block">
              {session.user.email}
            </span>
          </div>
        </header>
        <main id="main-content" className="flex flex-1 flex-col">
          {children}
        </main>
      </div>
    );
  }

  if (session && (pathname === '/admin' || pathname.startsWith('/admin/'))) {
    return <StaffShell>{children}</StaffShell>;
  }

  const isPublic = !session || PUBLIC_PATHS.includes(pathname);
  const hasOwnDisclaimer = AUTH_PATHS.includes(pathname);

  if (isPublic) {
    return (
      <div className="flex min-h-dvh flex-col bg-paper text-ink">
        <SkipLink />
        <header className="sticky top-0 z-40 border-b border-border bg-surface">
          <div className="mx-auto flex h-16 w-full max-w-content items-center justify-between gap-3 px-4 sm:px-8">
            <Wordmark />
            <nav aria-label="Primary" className="flex items-center gap-2">
              {session ? (
                <Link
                  to="/dashboard"
                  className="focus-ring inline-flex h-10 items-center rounded-control bg-primary px-4 text-[15px] font-bold text-white hover:bg-primary-hover"
                >
                  Go to dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/sign-in"
                    className="focus-ring inline-flex h-10 items-center rounded-control px-3 text-[15px] font-bold text-ink-2 hover:text-ink"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/sign-up"
                    className="focus-ring inline-flex h-10 items-center rounded-control bg-primary px-4 text-[15px] font-bold text-white hover:bg-primary-hover"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </nav>
          </div>
        </header>

        <main id="main-content" className="flex flex-1 flex-col">
          {children}
        </main>

        {hasOwnDisclaimer ? null : <Disclaimer />}
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-paper text-ink">
      <SkipLink />

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-sidebar border-r border-border bg-surface lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-surface px-4 lg:hidden">
        <Wordmark compact />
        <span className="flex-1" />
        <PlanStatusPill />
        <NotificationBell to="/notifications" />
        {staff ? (
          <Link
            to="/admin"
            aria-label="Staff settings"
            className="focus-ring grid size-11 place-items-center rounded-control text-ink"
          >
            <Icon icon={Settings} size={22} />
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setMenuOpen(true);
          }}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          className="focus-ring grid size-11 place-items-center rounded-control text-ink"
        >
          <Icon icon={Menu} size={22} />
        </button>
      </header>

      {/* Mobile drawer */}
      {menuOpen ? (
        <div
          className="fixed inset-0 z-50 bg-ink/50 lg:hidden"
          onClick={(event) => {
            if (event.target === event.currentTarget) setMenuOpen(false);
          }}
        >
          <div className="h-full w-72 max-w-[85vw] overflow-y-auto bg-surface">
            <div className="flex justify-end p-2">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                }}
                aria-label="Close menu"
                className="focus-ring grid size-11 place-items-center rounded-control text-ink"
              >
                <Icon icon={X} size={22} />
              </button>
            </div>
            <SidebarContent
              onNavigate={() => {
                setMenuOpen(false);
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="lg:pl-[var(--width-sidebar)]">
        {/* Desktop top bar: plan status and the bell, at the right of the page. */}
        <header className="sticky top-0 z-20 hidden h-14 items-center justify-end gap-3 border-b border-border bg-surface px-8 lg:flex">
          <PlanStatusBar />
          <NotificationBell to="/notifications" />
        </header>
        <main
          id="main-content"
          className="mx-auto w-full max-w-content px-4 pb-24 pt-6 sm:px-8 lg:pb-12"
        >
          <VerifyEmailBanner />
          {children}
        </main>
        <div className="hidden lg:block">
          <Disclaimer />
        </div>
      </div>

      {/* Mobile bottom bar — student destinations only. */}
      <nav
        aria-label="Sections"
        className="fixed inset-x-0 bottom-0 z-30 flex h-16 border-t border-border bg-surface lg:hidden"
      >
        {mobileNav.map((entry) => (
          <MobileNavLink key={entry.label} entry={entry} />
        ))}
      </nav>
    </div>
  );
}

function MobileNavLink({ entry }: { entry: NavEntry }) {
  const active = useActive(entry);

  return (
    <Link
      to={entry.to as '/dashboard'}
      search={(entry.search ?? {}) as PracticeSearch}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-1 text-overline font-bold',
        active ? 'text-primary' : 'text-muted',
      )}
    >
      <Icon icon={entry.icon} size={20} />
      {entry.label}
    </Link>
  );
}
